import { MapPin } from "lucide-react";
import { useState } from "react";

import { CardActions, createCardActionsProps } from "@/src/components/cards/card-actions";
import { EntityDropdownMenu, Field, FieldLayout } from "@/src/components/cards/common";
import LocationCardEditable from "@/src/components/cards/location-card/location-card-editable";
import { ContextMenu, ContextMenuTrigger } from "@/src/components/shadcn/ui/context-menu";
import { ValerieCard } from "@/src/components/valerie-card";
import { useUser } from "@/src/context/user-context";
import { canWriteSOR } from "@/src/lib/rbac";
import { Location } from "@/src/models/location";

const LOCATION_FIELDS: Field[] = [
  { key: "name", label: "Name", colSpan: 2, section: "Details" },
  { key: "ehrId", label: "EHR ID", section: "Details" },
  { key: "facilityType", label: "Facility Type", section: "Details" },
  { key: "address.line1", label: "Address", colSpan: 2, section: "Address" },
  { key: "address.line2", label: "Address 2", section: "Address" },
  { key: "address.city", label: "City", startsRow: true, section: "Address" },
  { key: "address.state", label: "State", section: "Address" },
  { key: "address.postalCode", label: "Zip Code", section: "Address" },
  { key: "phoneNumber", label: "Phone", type: "tel", section: "Contact" },
  { key: "faxPhoneNumber", label: "Fax", type: "tel", section: "Contact" },
];

export default function LocationCard({
  location,
  type,
  isEditable = false,
  onEditLocation,
  onEditLocationLoading,
}: {
  location: Location;
  type: string;
  isEditable?: boolean;
  // Callback for when user switches to a different location - allows parent to handle the context update
  onEditLocation?: (updatedLocation: Location) => Promise<void>;
  onEditLocationLoading?: boolean;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const { roles } = useUser();
  const canWrite = canWriteSOR(roles);
  const effectiveIsEditable = isEditable && canWrite;

  if (!location) {
    return null;
  }

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  if (isEditing) {
    return (
      <LocationCardEditable
        location={location}
        onClose={handleEditToggle}
        type={type}
        onEditLocation={onEditLocation}
        onEditLocationLoading={onEditLocationLoading}
      />
    );
  }

  const cardActionsProps = createCardActionsProps({
    id: location.id,
    label: "Location ID",
    href: `/locations/${location.displayId}`,
    customProps: [{ label: "Edit Location", onClick: handleEditToggle }],
  });

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <ValerieCard
          className="w-full"
          icon={<MapPin />}
          title={`${type} Location`}
          actions={effectiveIsEditable ? <EntityDropdownMenu cardActionsProps={cardActionsProps} /> : null}
        >
          <FieldLayout fields={LOCATION_FIELDS} patient={location} />
        </ValerieCard>
      </ContextMenuTrigger>
      {effectiveIsEditable && <CardActions {...cardActionsProps} isContextMenu={true} />}
    </ContextMenu>
  );
}
